/**
 * PATH: packages/ui/src/kanban/components/card-detail/CardBlockersSection.tsx
 * FILE_ID: UI-KANBAN-CMP-CARD-BLOCKERS-001
 * PURPOSE: Card dependency ("Blocked by" / predecessors) CRUD for a single
 *          Kanban card — list predecessors, add/remove a blocker.
 * WHY:     Operator 2026-06-30 kanban audit: "Dependencies / blockers only
 *          exist on cash-flow boards". The CRUD used to live INSIDE
 *          CardCashFlowSection, which only renders when board.cash_flow_enabled
 *          — so normal boards had no blocker UI at all. Extracted here so
 *          EVERY board gets blocker management. Cash-flow boards embed this
 *          (variant='bare', identical look); normal boards render it standalone
 *          (variant='card').
 * FLOW:
 *   ┌───────────────┐ create/delete ┌────────────────────────┐ invalidate ┌────────┐
 *   │ pick / remove │ ────────────▶ │ api.{create,delete}Dep │ ─────────▶ │ re-list│
 *   └───────────────┘               └────────────────────────┘            └────────┘
 * DEPENDENCIES:
 *   - @tanstack/react-query: list + mutate, cache key (boardId, cardId) shared
 *     with the Cash Flow DAG so both stay in sync.
 *   - ../../api: KanbanApi (listCardDependencies / createCardDependency /
 *     deleteCardDependency) — all OPTIONAL; returns null when the host wired
 *     neither list nor create.
 *   - ../../types: Card, CardDependency
 * RELATED:
 *   - CardCashFlowSection.tsx: embeds this (variant='bare') under the cash
 *     amount/direction editor on cash-flow boards.
 *   - card-detail/drawer-sections/CardTabContent.tsx: renders this standalone
 *     (variant='card') when cash flow is OFF, so blockers exist on every board.
 *   - BoardCashFlowView / BoardCashFlowDag: read the same CardDependency rows.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'

import type { KanbanApi } from '../../api'
import type { Card, CardDependency } from '../../types'

export interface CardBlockersSectionProps {
  card: Card
  api: KanbanApi
  /** Parent board id — scopes the dependency query + invalidation so peers
   *  don't refetch each other (and keeps parity with the Cash Flow DAG). */
  boardId: string | undefined
  /** All other cards on this board; the picker filters to "not this card AND
   *  not already a blocker". */
  boardCards: Card[] | undefined
  /** 'card' (default) renders a standalone bordered section for normal boards;
   *  'bare' renders just the inner block for embedding inside
   *  CardCashFlowSection so cash-flow boards stay visually unchanged. */
  variant?: 'card' | 'bare'
}

export function CardBlockersSection({
  card,
  api,
  boardId,
  boardCards,
  variant = 'card',
}: CardBlockersSectionProps) {
  const qc = useQueryClient()
  const [predecessorPick, setPredecessorPick] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  // Predecessors of this card = edges where `to_card === card.id`. The viewset
  // returns edges where either endpoint matches, so we narrow client-side.
  // Same cache key as CardCashFlowSection used → no behaviour change for the
  // Cash Flow DAG which reads/invalidates the same prefix.
  const depsQ = useQuery({
    queryKey: ['kanban', 'card-dependencies', boardId, card.id],
    queryFn: () => api.listCardDependencies!({ cardId: card.id }),
    enabled: !!api.listCardDependencies,
  })

  const predecessors: CardDependency[] = useMemo(() => {
    const list = depsQ.data ?? []
    return list.filter((d) => d.to_card === card.id)
  }, [depsQ.data, card.id])

  const predecessorIds = useMemo(
    () => new Set(predecessors.map((d) => d.from_card)),
    [predecessors],
  )

  const cardLookup = useMemo(() => {
    const m = new Map<string, Card>()
    for (const c of boardCards ?? []) m.set(c.id, c)
    return m
  }, [boardCards])

  const eligibleCards = useMemo(() => {
    return (boardCards ?? []).filter(
      (c) => c.id !== card.id && !predecessorIds.has(c.id),
    )
  }, [boardCards, card.id, predecessorIds])

  const addDepMut = useMutation({
    mutationFn: async (fromCardId: string) => {
      if (!api.createCardDependency) throw new Error('Host did not wire createCardDependency')
      return api.createCardDependency({ from_card: fromCardId, to_card: card.id })
    },
    onSuccess: () => {
      setError(null)
      setPredecessorPick('')
      void qc.invalidateQueries({ queryKey: ['kanban', 'card-dependencies', boardId] })
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : 'Could not add blocker')
    },
  })

  const removeDepMut = useMutation({
    mutationFn: async (depId: string) => {
      if (!api.deleteCardDependency) throw new Error('Host did not wire deleteCardDependency')
      await api.deleteCardDependency(depId)
    },
    onSuccess: () => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['kanban', 'card-dependencies', boardId] })
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : 'Could not remove blocker')
    },
  })

  // Nothing to manage if the host wired neither read nor create.
  if (!api.listCardDependencies && !api.createCardDependency) return null

  const body = (
    <div className="space-y-1.5">
      <span className="text-[10px] uppercase tracking-wide text-neutral-500">
        Blocked by ({predecessors.length})
      </span>
      {predecessors.length === 0 && (
        <p className="text-[11px] text-neutral-400 italic">
          No blockers. Add one to mark a dependency.
        </p>
      )}
      {predecessors.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {predecessors.map((dep) => {
            const src = cardLookup.get(dep.from_card)
            const label = src?.title ?? `card ${dep.from_card.slice(0, 8)}…`
            return (
              <li
                key={dep.id}
                className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-neutral-900 px-2 py-0.5 text-[11px] ring-1 ring-neutral-200 dark:ring-neutral-700"
              >
                <span className="max-w-[160px] truncate" title={label}>{label}</span>
                <button
                  type="button"
                  onClick={() => removeDepMut.mutate(dep.id)}
                  disabled={removeDepMut.isPending}
                  aria-label={`Remove blocker ${label}`}
                  className="text-neutral-400 hover:text-red-600"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {!!api.createCardDependency && eligibleCards.length > 0 && (
        <div className="flex items-center gap-1.5">
          <select
            value={predecessorPick}
            onChange={(e) => setPredecessorPick(e.target.value)}
            disabled={addDepMut.isPending}
            className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-[11px]"
            aria-label="Pick a card to add as a blocker"
          >
            <option value="">Add blocker…</option>
            {eligibleCards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title || `(untitled ${c.id.slice(0, 8)})`}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => predecessorPick && addDepMut.mutate(predecessorPick)}
            disabled={!predecessorPick || addDepMut.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
            aria-label="Confirm adding selected blocker"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="text-[11px] text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  )

  if (variant === 'bare') return body

  return (
    <section
      data-testid="card-blockers-section"
      className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40 px-3 py-3"
    >
      {body}
    </section>
  )
}
